import { createTheme, Theme } from '@mui/material/styles';

export type ThemeName = 'dark' | 'light' | 'ocean';

export const getMuiTheme = (name: ThemeName): Theme => {
  if (name === 'light') {
    return createTheme({
      palette: {
        mode: 'light',
        primary: { main: '#FF7316', contrastText: '#0b1220' },
        secondary: { main: '#0ea5e9', contrastText: '#0b1220' },
        background: { default: '#f1f5f9', paper: '#ffffff' },
        text: { primary: '#0b1220', secondary: '#334155' },
        divider: 'rgba(2, 6, 23, 0.12)'
      },
      typography: {
        fontFamily: '"Aldrich", sans-serif;',
        h1: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 2.5vw + 0.6rem, 2.125rem)' },
        h5: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.125rem, 1.8vw + 0.3rem, 1.5rem)' },
        h6: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(0.95rem, 1.5vw + 0.25rem, 1.25rem)' },
        button: { fontFamily: '"Aldrich", sans-serif' },
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 6px 14px rgba(2,6,23,0.06)',
              borderRadius: 10,
              borderBottom: '2px solid rgba(2,6,23,0.08)'
            }
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 3,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 14px rgba(2,6,23,0.08)' },
            },
            containedPrimary: {
              color: '#0b1220',
              '&:hover': { backgroundColor: '#ff8a3f' }
            },
            containedSecondary: {
              color: '#0b1220'
            },
            outlinedPrimary: {
              color: '#FF7316',
              borderColor: '#ff7316',
              '&:hover': { backgroundColor: 'rgba(255,115,22,0.06)' }
            }
          }
        },
        MuiPaper: {
          styleOverrides: { root: { backgroundImage: 'none' } }
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: 'rgba(2,6,23,0.06)'
              },
              '&:hover': {
                backgroundColor: 'rgba(2,6,23,0.04)'
              }
            }
          }
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: '#ffffff',
              color: '#0b1220',
              borderBottom: '1px solid rgba(2,6,23,0.1)'
            }
          }
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(2,6,23,0.08)',
              boxShadow: '0 10px 30px rgba(2,6,23,0.12)'
            }
          }
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(2,6,23,0.08)'
            }
          }
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(10px)'
            }
          }
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)'
            }
          }
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(2,6,23,0.35)',
              backdropFilter: 'blur(2px)'
            }
          }
        }
      }
    });
  }

  if (name === 'ocean') {
    return createTheme({
      palette: {
        mode: 'dark',
        primary: { main: '#22d3ee', contrastText: '#0b1220' },
        secondary: { main: '#7c3aed', contrastText: '#ffffff' },
        background: { default: '#0b1220', paper: '#0f172a' },
        text: { primary: '#e5e7eb', secondary: '#cbd5e1' },
      },
      typography: {
        fontFamily: '"Aldrich", sans-serif;',
        h1: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 2.5vw + 0.6rem, 2.125rem)' },
        h5: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.125rem, 1.8vw + 0.3rem, 1.5rem)' },
        h6: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(0.95rem, 1.5vw + 0.25rem, 1.25rem)' },
        button: { fontFamily: '"Aldrich", sans-serif' },
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              borderRadius: 10,
              borderBottom: '2px solid rgba(124, 58, 237, 0.35)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 22px 48px rgba(34, 211, 238, 0.28)'
              }
            }
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 3,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(34, 211, 238, 0.35)' },
            },
          }
        },
        MuiPaper: {
          styleOverrides: { root: { backgroundImage: 'none' } }
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateX(4px)',
                backgroundColor: 'rgba(34,211,238,0.08)',
                boxShadow: '0 10px 24px rgba(34,211,238,0.15)'
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(124,58,237,0.15)'
              }
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: {
              border: '1px solid rgba(34,211,238,0.28)',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 20px rgba(34,211,238,0.25)',
                transform: 'translateY(-1px)'
              }
            }
          }
        }
        ,
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(15,23,42,0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(124,58,237,0.25)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
            }
          }
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(15,23,42,0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(124,58,237,0.25)'
            }
          }
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(15,23,42,0.85)',
              backdropFilter: 'blur(10px)'
            }
          }
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: {
              backgroundColor: 'rgba(15,23,42,0.85)',
              backdropFilter: 'blur(8px)'
            }
          }
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(2px)'
            }
          }
        }
      }
    });
  }

  // default dark
  return createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#FF7316', contrastText: '#ffffff' },
      secondary: { main: '#1ce9e0', contrastText: '#0b1a19' },
      background: { default: '#0e1918', paper: 'rgba(15, 41, 39, 0.45)' },
      text: { primary: '#ffffff', secondary: '#cbd5e1' },
    },
    typography: {
      fontFamily: '"Aldrich", sans-serif;',
      h1: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
      h2: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
      h3: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700 },
      h4: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 2.5vw + 0.6rem, 2.125rem)' },
      h5: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(1.125rem, 1.8vw + 0.3rem, 1.5rem)' },
      h6: { fontFamily: '"Aldrich", sans-serif', fontWeight: 700, fontSize: 'clamp(0.95rem, 1.5vw + 0.25rem, 1.25rem)' },
      button: { fontFamily: '"Aldrich", sans-serif' },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
            borderRadius: 10,
            borderBottom: '2px solid rgba(28, 233, 224, 0.25)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 22px 48px rgba(28, 233, 224, 0.28)'
            }
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 3,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(28, 233, 224, 0.35)' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none', backgroundColor: 'rgba(15, 41, 39, 0.45)' } }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateX(4px)',
              backgroundColor: 'rgba(28,233,224,0.08)',
              boxShadow: '0 10px 24px rgba(28,233,224,0.15)'
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(28,233,224,0.12)'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            border: '1px solid rgba(28,233,224,0.28)',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
            '&:hover': {
              boxShadow: '0 8px 20px rgba(28,233,224,0.25)',
              transform: 'translateY(-1px)'
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(15,41,39,0.65)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(28,233,224,0.25)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(15,41,39,0.65)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(28,233,224,0.25)'
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(15,41,39,0.7)',
            backdropFilter: 'blur(10px)'
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(15,41,39,0.7)',
            backdropFilter: 'blur(8px)'
          }
        }
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(2px)'
          }
        }
      }
    }
  });
};


